import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { IAdminPageView, useAdminUserPageViewsQuery } from '../../api/admin';

const DAYS = 30;
const RECENT_DAYS = 7;
const MAX_TOOLTIP_ROWS = 12;
/** The page-views endpoint returns at most this many rows (newest first). */
const PAGE_VIEWS_FETCH_LIMIT = 2000;
const CHART_HEIGHT = 170;

interface DayBucket {
	key: string;
	date: Date;
	views: IAdminPageView[];
}

function dayKey(d: Date): string {
	return d.toLocaleDateString('en-CA');
}

function DayTooltip({ day }: { day: DayBucket }) {
	const n = day.views.length;
	// API returns newest first; a day's timeline reads better oldest first
	const rows = day.views.slice(0, MAX_TOOLTIP_ROWS).reverse();
	return (
		<Box sx={{ p: 0.5, maxWidth: 420 }}>
			<Typography variant="body2" fontWeight={600} sx={{ mb: n > 0 ? 0.5 : 0 }}>
				{day.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} · {n} page view
				{n !== 1 ? 's' : ''}
			</Typography>
			{rows.map((pv, i) => (
				<Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
					<Typography variant="caption" color="text.secondary" sx={{ minWidth: 52, flexShrink: 0 }}>
						{new Date(pv.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
					</Typography>
					<Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
						{pv.page}
					</Typography>
					{pv.info && (
						<Typography variant="caption" color="text.secondary" noWrap>
							{pv.info}
						</Typography>
					)}
				</Box>
			))}
			{n > rows.length && (
				<Typography variant="caption" color="text.secondary">
					+{n - rows.length} more (see the Page Views table below)
				</Typography>
			)}
		</Box>
	);
}

interface Props {
	userId: number;
}

/** Page views per day for the last 30 days. Hover a day for the individual page views. */
export default function UserPageViewsChart({ userId }: Props) {
	const { data: pageViews, isLoading } = useAdminUserPageViewsQuery(userId);

	const days = useMemo<DayBucket[]>(() => {
		const byDay = new Map<string, IAdminPageView[]>();
		for (const pv of pageViews ?? []) {
			const k = dayKey(new Date(pv.created_at));
			const bucket = byDay.get(k);
			if (bucket) bucket.push(pv);
			else byDay.set(k, [pv]);
		}
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const out: DayBucket[] = [];
		for (let i = DAYS - 1; i >= 0; i--) {
			const d = new Date(today);
			d.setDate(today.getDate() - i);
			const k = dayKey(d);
			out.push({ key: k, date: d, views: byDay.get(k) ?? [] });
		}
		return out;
	}, [pageViews]);

	const max = Math.max(1, ...days.map((d) => d.views.length));
	const total30 = days.reduce((sum, d) => sum + d.views.length, 0);
	const total7 = days.slice(-RECENT_DAYS).reduce((sum, d) => sum + d.views.length, 0);
	const truncated = (pageViews?.length ?? 0) >= PAGE_VIEWS_FETCH_LIMIT;

	return (
		<Box>
			{isLoading ? (
				<Skeleton variant="rectangular" height={CHART_HEIGHT} />
			) : (
				<Box sx={{ display: 'flex', gap: 1 }}>
					{/* y axis */}
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'space-between',
							height: CHART_HEIGHT,
							pr: 0.5,
							flexShrink: 0,
						}}
					>
						<Typography variant="caption" color="text.secondary">{max}</Typography>
						<Typography variant="caption" color="text.secondary">0</Typography>
					</Box>

					<Box sx={{ flex: 1, minWidth: 0 }}>
						{/* bars */}
						<Box
							sx={{
								display: 'flex',
								alignItems: 'stretch',
								gap: '3px',
								height: CHART_HEIGHT,
								borderBottom: 1,
								borderColor: 'divider',
							}}
						>
							{days.map((d, i) => {
								const recent = i >= DAYS - RECENT_DAYS;
								return (
									<Tooltip
										key={d.key}
										title={<DayTooltip day={d} />}
										placement="top"
										arrow
										componentsProps={{ tooltip: { sx: { maxWidth: 'none' } } }}
									>
										<Box
											sx={{
												flex: 1,
												height: '100%',
												display: 'flex',
												flexDirection: 'column',
												justifyContent: 'flex-end',
												cursor: 'default',
												borderLeft: i === DAYS - RECENT_DAYS ? '1px dashed' : 'none',
												borderColor: 'divider',
												'&:hover > div': { opacity: 1 },
											}}
										>
											<Box
												sx={{
													height: `${(d.views.length / max) * 100}%`,
													minHeight: d.views.length > 0 ? 3 : 0,
													bgcolor: recent ? 'primary.main' : 'primary.dark',
													opacity: 0.75,
													borderRadius: '2px 2px 0 0',
													transition: 'opacity 120ms',
												}}
											/>
										</Box>
									</Tooltip>
								);
							})}
						</Box>
						{/* x axis: a label every 5 days, plus the last-7-days marker */}
						<Box sx={{ display: 'flex', gap: '3px', mt: 0.5 }}>
							{days.map((d, i) => (
								<Box key={d.key} sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
									{(i === 0 || i === DAYS - 1 || (DAYS - 1 - i) % 5 === 0) && (
										<Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
											{d.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
										</Typography>
									)}
								</Box>
							))}
						</Box>
					</Box>
				</Box>
			)}

			<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
				{total30.toLocaleString()} page view{total30 !== 1 ? 's' : ''} in the last 30 days, {total7.toLocaleString()} in the
				last 7 (right of the dashed line). Hover a day to see which pages were opened.
				{truncated && ' Only the most recent 2,000 page views are loaded, so older days may be incomplete.'}
			</Typography>
		</Box>
	);
}
